import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileDown, XCircle } from 'lucide-react';
import { Dialog } from '@components/Dialog';
import { Button } from '@components/Button';
import { salesOrderService } from '../../services/salesOrderService';
import type { CardPayment, CardPaymentsResponse, CardRefundResult } from '../../types/cardRefund';
import {
  brandLabel,
  centsToMaskedInput,
  formatCentsBRL,
  formatIsoDateTimeBR,
  isRefundable,
  resolveRefundAmount,
  RefundMode,
  typedToCents,
} from './cardRefund';

interface CardRefundModalProps {
  isOpen: boolean;
  orderId: number;
  onClose: () => void;
  /** Chamado depois de qualquer tentativa registrada, para a lista de estornos recarregar. */
  onRefunded: () => void;
}

const Detail: React.FC<{ label: string; children: React.ReactNode; mono?: boolean }> = ({ label, children, mono }) => (
  <div>
    <dt className="text-xs text-slate-500 uppercase tracking-wider">{label}</dt>
    <dd className={`mt-0.5 text-sm text-slate-900 ${mono ? 'font-mono' : ''}`}>{children || '—'}</dd>
  </div>
);

/**
 * Estorno do cartão de crédito do pedido (somente admin).
 *
 * Mostra os dados do pagamento como o PaymentHub gravou (cliente, portador,
 * bandeira, final do cartão, TID, NSU, autorização) e pede o estorno total ou
 * parcial. A Cielo estorna no estabelecimento que capturou; cada tentativa fica
 * registrada e gera comprovante em PDF.
 */
export const CardRefundModal: React.FC<CardRefundModalProps> = ({ isOpen, orderId, onClose, onRefunded }) => {
  const [data, setData] = useState<CardPaymentsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [mode, setMode] = useState<RefundMode>('total');
  const [typedCents, setTypedCents] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<CardRefundResult | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setData(null);
    setLoadError(null);
    setSelectedId(null);
    setMode('total');
    setTypedCents(null);
    setConfirming(false);
    setSubmitError(null);
    setResult(null);
    setLoading(true);
    salesOrderService
      .getCardPayments(orderId)
      .then((resp) => {
        if (cancelled) return;
        setData(resp);
        const first = resp.payments.find(isRefundable) ?? resp.payments[0];
        setSelectedId(first ? first.credit_card_payment_id : null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Erro ao consultar os pagamentos de cartão');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, orderId]);

  const payment: CardPayment | undefined = useMemo(
    () => data?.payments.find((p) => p.credit_card_payment_id === selectedId),
    [data, selectedId],
  );
  const amount = payment ? resolveRefundAmount(payment, mode, typedCents) : null;
  const amountError = amount && 'error' in amount ? amount.error : null;
  const amountCents = amount && 'cents' in amount ? amount.cents : 0;

  const handleSubmit = async () => {
    if (!payment || amountCents <= 0) return;
    setSaving(true);
    setSubmitError(null);
    try {
      const res = await salesOrderService.refundCard(orderId, {
        credit_card_payment_id: payment.credit_card_payment_id,
        amount_cents: amountCents,
        total: mode === 'total',
      });
      setResult(res);
      if (res.refund) onRefunded();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Erro ao estornar o cartão');
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  };

  const handlePdf = async (refundId: number) => {
    setPdfLoading(true);
    try {
      await salesOrderService.downloadCardRefundPdf(orderId, refundId);
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Erro ao gerar o comprovante');
    } finally {
      setPdfLoading(false);
    }
  };

  const footer = result ? (
    <div className="flex justify-end gap-2">
      {result.refund && (
        <Button variant="outline" icon={<FileDown size={16} />} loading={pdfLoading} onClick={() => handlePdf(result.refund!.credit_card_refund_id)}>
          {result.success ? 'Baixar comprovante' : 'Baixar relatório da tentativa'}
        </Button>
      )}
      <Button onClick={onClose}>Fechar</Button>
    </div>
  ) : confirming ? (
    <div className="flex justify-end gap-2">
      <Button variant="outline" onClick={() => setConfirming(false)} disabled={saving}>
        Voltar
      </Button>
      <Button variant="danger" loading={saving} onClick={handleSubmit}>
        Confirmar estorno de {formatCentsBRL(amountCents)}
      </Button>
    </div>
  ) : (
    <div className="flex justify-end gap-2">
      <Button variant="outline" onClick={onClose}>
        Cancelar
      </Button>
      <Button variant="danger" disabled={!payment || !!amountError || loading} onClick={() => setConfirming(true)}>
        Estornar
      </Button>
    </div>
  );

  return (
    <Dialog isOpen={isOpen} onClose={saving ? () => {} : onClose} title="Estornar cartão de crédito" size="lg" footer={footer}>
      {loading && <p className="text-sm text-slate-500">Consultando pagamentos no PaymentHub…</p>}
      {loadError && <p className="text-sm text-red-600">{loadError}</p>}

      {!loading && data && data.payments.length === 0 && (
        <p className="text-sm text-slate-600">{data.message || 'Nenhum pagamento de cartão encontrado para este pedido.'}</p>
      )}

      {result && (
        <div className={`mb-4 rounded-md border p-3 ${result.success ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
          <div className="flex items-center gap-2 font-medium text-sm">
            {result.success ? <CheckCircle2 size={18} className="text-emerald-600" /> : <XCircle size={18} className="text-amber-600" />}
            {result.message}
          </div>
          {result.refund && (
            <dl className="mt-3 grid grid-cols-2 gap-3">
              <Detail label="Valor">{formatCentsBRL(result.refund.amount_cents)} ({result.refund.is_total ? 'total' : 'parcial'})</Detail>
              <Detail label="Situação">{result.refund.status_label}</Detail>
              <Detail label="Código da transação (TID)" mono>{result.refund.tid}</Detail>
              <Detail label="NSU do cancelamento" mono>{result.refund.proof_of_sale}</Detail>
              <Detail label="Código de retorno" mono>
                {[result.refund.provider_return_code || result.refund.return_code, result.refund.provider_return_message || result.refund.return_message]
                  .filter(Boolean)
                  .join(' — ')}
              </Detail>
            </dl>
          )}
        </div>
      )}

      {!result && data && data.payments.length > 0 && (
        <div className="space-y-4">
          <dl className="grid grid-cols-2 gap-3">
            <Detail label="Cliente">{data.customer_name}</Detail>
          </dl>

          {data.payments.length > 1 && (
            <div>
              <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Pagamento</label>
              <select
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                value={selectedId ?? ''}
                onChange={(e) => setSelectedId(Number(e.target.value))}
                disabled={confirming}
              >
                {data.payments.map((p) => (
                  <option key={p.credit_card_payment_id} value={p.credit_card_payment_id}>
                    {brandLabel(p.brand)} final {p.last4} · {formatCentsBRL(p.amount_cents)} · {formatIsoDateTimeBR(p.transaction_timestamp)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {payment && (
            <>
              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 rounded-md border border-slate-200 p-3">
                <Detail label="Valor da venda">{formatCentsBRL(payment.amount_cents)}</Detail>
                <Detail label="Parcelas">{payment.installments > 1 ? `${payment.installments}x` : 'À vista'}</Detail>
                <Detail label="Data">{formatIsoDateTimeBR(payment.transaction_timestamp)}</Detail>
                <Detail label="Cartão">
                  {brandLabel(payment.brand)} •••• {payment.last4}
                </Detail>
                <Detail label="Portador">{payment.holder}</Detail>
                <Detail label="Situação">{payment.status_label}</Detail>
                <Detail label="TID" mono>{payment.tid}</Detail>
                <Detail label="NSU" mono>{payment.proof_of_sale}</Detail>
                <Detail label="Autorização" mono>{payment.authorization_code}</Detail>
                <Detail label="Já estornado">{formatCentsBRL(payment.refunded_cents)}</Detail>
                <Detail label="Saldo estornável">
                  <span className="font-semibold">{formatCentsBRL(payment.refundable_cents)}</span>
                </Detail>
                <Detail label="EC (estabelecimento)" mono>{payment.ec}</Detail>
              </dl>

              {isRefundable(payment) ? (
                <fieldset className="space-y-2" disabled={confirming}>
                  <legend className="text-xs text-slate-500 uppercase tracking-wider mb-1">Tipo de estorno</legend>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" name="refund-mode" checked={mode === 'total'} onChange={() => setMode('total')} />
                    Total ({formatCentsBRL(payment.refundable_cents)})
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" name="refund-mode" checked={mode === 'partial'} onChange={() => setMode('partial')} />
                    Parcial
                  </label>
                  {mode === 'partial' && (
                    <div className="pl-6">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500">R$</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="w-40 border border-slate-300 rounded px-2 py-1.5 text-sm text-right"
                          placeholder="0,00"
                          value={centsToMaskedInput(typedCents)}
                          onChange={(e) => setTypedCents(typedToCents(e.target.value))}
                          autoFocus
                        />
                      </div>
                    </div>
                  )}
                  {amountError && mode === 'partial' && typedCents != null && <p className="text-xs text-red-600">{amountError}</p>}
                </fieldset>
              ) : (
                <p className="text-sm text-slate-600">{amountError}</p>
              )}

              {confirming && (
                <div className="flex gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  <AlertTriangle size={18} className="shrink-0" />
                  <span>
                    Confirma o estorno <strong>{mode === 'total' ? 'total' : 'parcial'}</strong> de <strong>{formatCentsBRL(amountCents)}</strong> no cartão{' '}
                    {brandLabel(payment.brand)} final {payment.last4} de {data.customer_name}? A operação é feita na Cielo e não pode ser desfeita.
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {submitError && <p className="mt-3 text-sm text-red-600">{submitError}</p>}
    </Dialog>
  );
};
